/**
 * Authentication API services
 */

import apiClient from './client';
import type { User, TenantContext } from '@/types';

export interface MeResponse {
  user: User;
  currentTenant: TenantContext | null;
  availableTenants: AvailableTenant[];
  permissions: string[];
  isSuperAdmin: boolean;
  accessEmail: string;
}

export interface AvailableTenant {
  id: string;
  name: string;
  slug: string;
  role: string;
}

export interface SwitchTenantResponse {
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  roleId: string;
  roleName: string;
  permissions: string[];
}

function mapUser(data: any): User {
  return {
    id: data.id,
    email: data.email,
    fullName: data.full_name,
    isSuperAdmin: data.is_super_admin,
    isActive: data.is_active ?? true,
    emailVerifiedAt: data.email_verified_at ?? null,
    lastLoginAt: data.last_login_at ?? null,
  };
}

function mapTenant(data: any): TenantContext {
  return {
    tenantId: data.tenant_id ?? data.id,
    tenantName: data.name ?? data.tenant_name,
    tenantSlug: data.slug ?? data.tenant_slug,
    roleId: data.role_id ?? '',
    roleName: data.role_name ?? data.role,
    permissions: data.permissions ?? [],
  };
}

export const authApi = {
  getMe: async (): Promise<MeResponse> => {
    const response = await apiClient.get('/v1/auth/me');
    return {
      user: mapUser(response.data.user),
      currentTenant: response.data.current_tenant ? mapTenant(response.data.current_tenant) : null,
      availableTenants: (response.data.available_tenants ?? []).map((tenant: any) => ({
        id: tenant.id,
        name: tenant.name,
        slug: tenant.slug,
        role: tenant.role,
      })),
      permissions: response.data.permissions ?? [],
      isSuperAdmin: response.data.is_super_admin,
      accessEmail: response.data.access_email,
    };
  },

  switchTenant: async (tenantId: string): Promise<SwitchTenantResponse> => {
    const response = await apiClient.post('/v1/auth/switch-tenant', {
      tenant_id: tenantId,
    });
    return mapTenant(response.data);
  },

  /**
   * Bootstrap admin - Grant super admin privileges
   * Only works if no super admins exist yet, or with valid bootstrap token
   */
  bootstrapAdmin: async (email: string, bootstrapToken?: string): Promise<{
    message: string;
    user?: { id: string; email: string; fullName: string; isSuperAdmin: boolean };
  }> => {
    const response = await apiClient.post('/v1/auth/bootstrap-admin', {
      email,
      bootstrap_token: bootstrapToken,
    });
    return {
      message: response.data.message,
      user: response.data.user ? {
        id: response.data.user.id,
        email: response.data.user.email,
        fullName: response.data.user.full_name,
        isSuperAdmin: response.data.user.is_super_admin,
      } : undefined,
    };
  },
};
